import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { createApiClient, type ApiClient } from "@ferrivia/api-contract";
import { createTracker, type Tracker, type TrackerOptions } from "@ferrivia/analytics/browser";
import { useTranslation } from "@ferrivia/i18n";
import { createPasskeyDeviceStore, type PasskeyDeviceStore } from "./passkeyDevice";
import { createTokenStore, type TokenStore } from "./tokenStore";
import { SessionContext, useSessionState, type CachedSessionSeed } from "./useSession";

const noopTracker: Tracker = { track: () => {} };

export interface AuthContextValue {
  api: ApiClient;
  tokenStore: TokenStore;
  tracker?: Tracker;
  /** Optional like `tracker`: callers that don't care get the shared default. */
  passkeyDevice?: PasskeyDeviceStore;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  baseUrl: string;
  storageKey?: string;
  passkeyStorageKey?: string;
  /** Which app is emitting sign-in events, per the analytics catalog's source enum. */
  source: TrackerOptions["source"];
  /** App-supplied on-device snapshot, read once at mount to boot the shell
      before the first `/me` resolves. Left optional and app-supplied — the
      cache format is app-specific (`apps/client/src/offline/cachedSession.ts`)
      and this shared package has no business knowing its shape. */
  readCachedSession?: () => CachedSessionSeed | null;
  /** Called after the shared 401 handling (token clear, session reset) has
      already run. AuthProvider can't reach into an app's on-device cache
      itself, so an app that caches anything session-keyed purges it here. */
  onUnauthorized?: () => void;
  children: ReactNode;
}

export function AuthProvider({
  baseUrl,
  storageKey,
  passkeyStorageKey,
  source,
  readCachedSession,
  onUnauthorized,
  children,
}: AuthProviderProps) {
  const { i18n } = useTranslation();
  // The api client needs an onUnauthorized callback at construction time,
  // before useSessionState (which owns the actual reset logic) exists — a
  // ref breaks the chicken/egg without recreating the api client on every
  // session change.
  const unauthorizedRef = useRef<() => void>(() => {});

  const value = useMemo<AuthContextValue>(() => {
    const tokenStore = createTokenStore(storageKey);
    const api = createApiClient({
      baseUrl,
      getToken: tokenStore.get,
      onUnauthorized: () => unauthorizedRef.current(),
    });
    const tracker = createTracker({
      endpoint: `${baseUrl}/events/collect`,
      source,
      getToken: tokenStore.get,
      getUserId: tokenStore.get,
      getContext: () => ({ locale: i18n.language }),
    });
    return { api, tokenStore, tracker, passkeyDevice: createPasskeyDeviceStore(passkeyStorageKey) };
  }, [baseUrl, storageKey, passkeyStorageKey, source, i18n]);

  const session = useSessionState(value.api, value.tokenStore, readCachedSession);

  useEffect(() => {
    unauthorizedRef.current = () => {
      session.handleUnauthorized();
      onUnauthorized?.();
    };
  }, [session, onUnauthorized]);

  return (
    <AuthContext.Provider value={value}>
      <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
    </AuthContext.Provider>
  );
}

function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useApi/useSession must be used within an AuthProvider");
  }
  return ctx;
}

export function useApi(): ApiClient {
  return useAuthContext().api;
}

export function useTracker(): Tracker {
  return useAuthContext().tracker ?? noopTracker;
}

// Same fallback shape as noopTracker, but a real store rather than a no-op:
// a context assembled without one (app tests, mostly) must still read and
// write the same browser-local marks the running app does.
let defaultPasskeyDevice: PasskeyDeviceStore | undefined;

export function usePasskeyDevice(): PasskeyDeviceStore {
  const fromContext = useAuthContext().passkeyDevice;
  if (fromContext) {
    return fromContext;
  }
  defaultPasskeyDevice ??= createPasskeyDeviceStore();
  return defaultPasskeyDevice;
}
